import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Check, Flag, History, Pencil, Eye, AlertTriangle, Loader2, RotateCcw, ChevronRight,
} from "lucide-react";
import { format } from "date-fns";

interface Section {
  id: string;
  section_key: string;
  title: string;
  anchor: string | null;
  content: string;
  sort: number;
  status: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  updated_at: string;
}

interface Revision {
  id: string;
  section_id: string;
  title: string;
  content: string;
  edited_by: string | null;
  edit_note: string | null;
  created_at: string;
  editor_name?: string;
}

interface FlagRow {
  id: string;
  section_id: string;
  note: string;
  flagged_by: string | null;
  status: "open" | "resolved";
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  flagged_by_name?: string;
}

export default function AdminBriefingCorpus() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [sections, setSections] = useState<Section[]>([]);
  const [flags, setFlags] = useState<FlagRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"read" | "history">("read");

  // edit state
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editNote, setEditNote] = useState("");
  const [saving, setSaving] = useState(false);

  // dialogs
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagNote, setFlagNote] = useState("");
  const [publishConfirm, setPublishConfirm] = useState(false);
  const [viewingRevision, setViewingRevision] = useState<Revision | null>(null);
  const [profilesMap, setProfilesMap] = useState<Record<string, string>>({});
  // Confirm dialogs replacing native confirm()
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null);
  const [confirmCancelEdit, setConfirmCancelEdit] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState<Revision | null>(null);
  // Busy guards
  const [reviewBusy, setReviewBusy] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);
  const [resolvingFlagId, setResolvingFlagId] = useState<string | null>(null);
  const [publishBusy, setPublishBusy] = useState(false);
  // Mobile rail toggle
  const [mobileRailOpen, setMobileRailOpen] = useState(false);

  const selected = sections.find((s) => s.id === selectedId) || null;

  const dirty = editing && selected
    ? (editTitle !== selected.title || editContent !== selected.content)
    : false;

  const loadAll = async () => {
    setLoading(true);
    const [{ data: secs }, { data: flg }] = await Promise.all([
      supabase.from("briefing_corpus").select("*").order("sort", { ascending: true }),
      supabase.from("briefing_corpus_flags").select("*").order("created_at", { ascending: false }),
    ]);
    setSections((secs as Section[]) || []);
    setFlags((flg as FlagRow[]) || []);
    setLoading(false);
    if (!selectedId && secs && secs.length > 0) setSelectedId(secs[0].id);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const loadRevisions = async (sectionId: string) => {
    const { data } = await supabase
      .from("briefing_corpus_revisions")
      .select("*")
      .eq("section_id", sectionId)
      .order("created_at", { ascending: false });
    const revs = (data as Revision[]) || [];
    setRevisions(revs);
    const ids = Array.from(new Set(revs.map((r) => r.edited_by).filter(Boolean))) as string[];
    const missing = ids.filter((id) => !profilesMap[id]);
    if (missing.length) {
      const { data: profs } = await supabase
        .from("profiles").select("id, full_name, email").in("id", missing);
      const next = { ...profilesMap };
      (profs || []).forEach((p: any) => { next[p.id] = p.full_name || p.email || "Admin"; });
      setProfilesMap(next);
    }
  };

  useEffect(() => {
    if (selectedId) loadRevisions(selectedId);
  }, [selectedId]);

  // navigation guard for unsaved
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const openFlags = useMemo(() => {
    const map: Record<string, number> = {};
    flags.forEach((f) => { if (f.status === "open") map[f.section_id] = (map[f.section_id] || 0) + 1; });
    return map;
  }, [flags]);

  const stats = useMemo(() => {
    const reviewed = sections.filter((s) => s.reviewed_at).length;
    const published = sections.filter((s) => s.status === "active").length;
    const openFlagCount = flags.filter((f) => f.status === "open").length;
    return { reviewed, published, openFlagCount, total: sections.length };
  }, [sections, flags]);

  const selectSection = (id: string) => {
    if (dirty) {
      setPendingSelectId(id);
      return;
    }
    setEditing(false);
    setEditNote("");
    setTab("read");
    setSelectedId(id);
    setMobileRailOpen(false);
  };

  const confirmSelectPending = () => {
    if (!pendingSelectId) return;
    setEditing(false);
    setEditNote("");
    setTab("read");
    setSelectedId(pendingSelectId);
    setPendingSelectId(null);
    setMobileRailOpen(false);
  };

  const startEdit = () => {
    if (!selected) return;
    setEditTitle(selected.title);
    setEditContent(selected.content);
    setEditNote("");
    setEditing(true);
  };

  const cancelEdit = () => {
    if (dirty) {
      setConfirmCancelEdit(true);
      return;
    }
    setEditing(false);
    setEditNote("");
  };

  const confirmDiscardEdit = () => {
    setEditing(false);
    setEditNote("");
    setConfirmCancelEdit(false);
  };

  const saveEdit = async (overrideNote?: string) => {
    if (!selected || !user) return;
    setSaving(true);
    try {
      const { error: upErr } = await supabase
        .from("briefing_corpus")
        .update({ title: editTitle, content: editContent, updated_at: new Date().toISOString() })
        .eq("id", selected.id);
      if (upErr) throw upErr;
      const { error: revErr } = await supabase
        .from("briefing_corpus_revisions")
        .insert({
          section_id: selected.id,
          title: editTitle,
          content: editContent,
          edited_by: user.id,
          edit_note: overrideNote ?? (editNote || null),
        });
      if (revErr) throw revErr;
      toast({ title: "Saved" });
      setEditing(false);
      setEditNote("");
      await loadAll();
      await loadRevisions(selected.id);
    } catch (e: any) {
      console.error("AdminBriefingCorpus save failed:", e);
      toast({ title: "Save didn't go through", description: "Try once more.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggleReviewed = async () => {
    if (!selected || !user || reviewBusy) return;
    setReviewBusy(true);
    const next = selected.reviewed_at ? null : new Date().toISOString();
    const { error } = await supabase
      .from("briefing_corpus")
      .update({ reviewed_at: next, reviewed_by: next ? user.id : null })
      .eq("id", selected.id);
    setReviewBusy(false);
    if (error) {
      console.error("AdminBriefingCorpus toggleReviewed failed:", error);
      toast({ title: "Review status didn't update", description: "Try again.", variant: "destructive" });
      return;
    }
    toast({ title: next ? "Marked reviewed" : "Review cleared" });
    loadAll();
  };

  const doPublishToggle = async () => {
    if (!selected || publishBusy) return;
    setPublishBusy(true);
    const next = selected.status === "active" ? "draft" : "active";
    const { error } = await supabase
      .from("briefing_corpus")
      .update({ status: next })
      .eq("id", selected.id);
    setPublishBusy(false);
    if (error) {
      console.error("AdminBriefingCorpus publish toggle failed:", error);
      toast({ title: "Publish state didn't change", description: "Try again.", variant: "destructive" });
      return;
    }
    toast({ title: next === "active" ? "Published" : "Unpublished" });
    setPublishConfirm(false);
    loadAll();
  };

  const requestPublishToggle = () => {
    if (!selected) return;
    if (selected.status === "active") {
      // unpublish immediately
      doPublishToggle();
    } else {
      setPublishConfirm(true);
    }
  };

  const submitFlag = async () => {
    if (!selected || !user || !flagNote.trim() || flagBusy) return;
    setFlagBusy(true);
    const { error } = await supabase.from("briefing_corpus_flags").insert({
      section_id: selected.id,
      note: flagNote.trim(),
      flagged_by: user.id,
    });
    setFlagBusy(false);
    if (error) {
      console.error("AdminBriefingCorpus submitFlag failed:", error);
      toast({ title: "Flag didn't post", description: "Try again.", variant: "destructive" });
      return;
    }
    toast({ title: "Flag added" });
    setFlagNote("");
    setFlagOpen(false);
    loadAll();
  };

  const resolveFlag = async (id: string) => {
    if (!user || resolvingFlagId) return;
    setResolvingFlagId(id);
    const { error } = await supabase
      .from("briefing_corpus_flags")
      .update({ status: "resolved", resolved_at: new Date().toISOString(), resolved_by: user.id })
      .eq("id", id);
    setResolvingFlagId(null);
    if (error) {
      console.error("AdminBriefingCorpus resolveFlag failed:", error);
      toast({ title: "Flag didn't resolve", description: "Try again.", variant: "destructive" });
      return;
    }
    loadAll();
  };

  const restoreRevision = async (rev: Revision) => {
    if (!selected || !user) return;
    setConfirmRestore(rev);
  };

  const doRestoreRevision = async (rev: Revision) => {
    if (!selected || !user) return;
    setConfirmRestore(null);
    setEditTitle(rev.title);
    setEditContent(rev.content);
    const restoreNote = `Restored version from ${format(new Date(rev.created_at), "PPpp")}`;
    setSaving(true);
    try {
      const { error: upErr } = await supabase
        .from("briefing_corpus")
        .update({ title: rev.title, content: rev.content, updated_at: new Date().toISOString() })
        .eq("id", selected.id);
      if (upErr) throw upErr;
      const { error: revErr } = await supabase
        .from("briefing_corpus_revisions")
        .insert({
          section_id: selected.id,
          title: rev.title,
          content: rev.content,
          edited_by: user.id,
          edit_note: restoreNote,
        });
      if (revErr) throw revErr;
      toast({ title: "Restored" });
      setViewingRevision(null);
      await loadAll();
      await loadRevisions(selected.id);
    } catch (e: any) {
      console.error("AdminBriefingCorpus restore failed:", e);
      toast({ title: "Restore didn't complete", description: "Try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const sectionFlags = selected ? flags.filter((f) => f.section_id === selected.id) : [];
  const openSectionFlags = sectionFlags.filter((f) => f.status === "open");
  const resolvedSectionFlags = sectionFlags.filter((f) => f.status === "resolved");
  const [resolvedExpanded, setResolvedExpanded] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-8rem)] gap-4">
      {/* Mobile section picker (below lg) */}
      <div className="lg:hidden">
        <Card className="p-3 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <h2 className="font-semibold text-sm truncate">
                {selected?.title || "Briefing Corpus"}
              </h2>
              <p className="text-[11px] text-muted-foreground">
                {stats.reviewed}/{stats.total} reviewed · {stats.published} published · {stats.openFlagCount} flags
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setMobileRailOpen((v) => !v)}
            >
              {mobileRailOpen ? "Hide" : "Sections"}
            </Button>
          </div>
          {mobileRailOpen && (
            <ScrollArea className="max-h-64">
              <div className="space-y-1">
                {sections.map((s) => {
                  const active = s.id === selectedId;
                  const flagCount = openFlags[s.id] || 0;
                  return (
                    <button
                      key={s.id}
                      onClick={() => selectSection(s.id)}
                      className={`w-full text-left p-2 rounded-md transition-colors ${active ? "bg-accent" : "hover:bg-muted"}`}
                    >
                      <div className="text-sm font-medium truncate">{s.title}</div>
                      <div className="mt-1 flex items-center gap-1 flex-wrap">
                        <Badge variant={s.status === "active" ? "default" : "secondary"} className="text-[10px] h-5">
                          {s.status === "active" ? "Published" : "Unpublished"}
                        </Badge>
                        {s.reviewed_at && (
                          <Badge variant="outline" className="text-[10px] h-5 gap-1">
                            <Check className="h-3 w-3" /> Reviewed
                          </Badge>
                        )}
                        {flagCount > 0 && (
                          <Badge variant="destructive" className="text-[10px] h-5 gap-1">
                            <Flag className="h-3 w-3" /> {flagCount}
                          </Badge>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </Card>
      </div>

      {/* Desktop left rail (lg+) */}
      <Card className="hidden lg:flex w-80 flex-col">
        <div className="p-4 border-b space-y-1">
          <h2 className="font-semibold">Briefing Corpus</h2>
          <p className="text-xs text-muted-foreground">
            {stats.reviewed} of {stats.total} reviewed, {stats.published} published, {stats.openFlagCount} open flags
          </p>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {sections.map((s) => {
              const active = s.id === selectedId;
              const flagCount = openFlags[s.id] || 0;
              return (
                <button
                  key={s.id}
                  onClick={() => selectSection(s.id)}
                  className={`w-full text-left p-2 rounded-md transition-colors ${active ? "bg-accent" : "hover:bg-muted"}`}
                >
                  <div className="text-sm font-medium truncate">{s.title}</div>
                  <div className="mt-1 flex items-center gap-1 flex-wrap">
                    <Badge variant={s.status === "active" ? "default" : "secondary"} className="text-[10px] h-5">
                      {s.status === "active" ? "Published" : "Unpublished"}
                    </Badge>
                    {s.reviewed_at && (
                      <Badge variant="outline" className="text-[10px] h-5 gap-1">
                        <Check className="h-3 w-3" /> Reviewed
                      </Badge>
                    )}
                    {flagCount > 0 && (
                      <Badge variant="destructive" className="text-[10px] h-5 gap-1">
                        <Flag className="h-3 w-3" /> {flagCount}
                      </Badge>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </Card>


      {/* Main pane */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Select a section to review.
          </div>
        ) : (
          <>
            <div className="p-4 border-b flex flex-wrap gap-2 items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={selected.status === "active" ? "default" : "secondary"}>
                  {selected.status === "active" ? "Published" : "Unpublished"}
                </Badge>
                {selected.reviewed_at && (
                  <Badge variant="outline" className="gap-1">
                    <Check className="h-3 w-3" /> Reviewed
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  Updated {format(new Date(selected.updated_at), "PP")}
                </span>
              </div>
              {!editing && (
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={startEdit}>
                    <Pencil className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setFlagOpen(true)}>
                    <Flag className="h-4 w-4 mr-1" /> Flag an issue
                  </Button>
                  <Button size="sm" variant="outline" onClick={toggleReviewed}>
                    <Check className="h-4 w-4 mr-1" />
                    {selected.reviewed_at ? "Clear review" : "Mark reviewed"}
                  </Button>
                  <Button
                    size="sm"
                    variant={selected.status === "active" ? "secondary" : "default"}
                    onClick={requestPublishToggle}
                  >
                    {selected.status === "active" ? "Unpublish" : "Publish"}
                  </Button>
                </div>
              )}
            </div>

            <Tabs value={tab} onValueChange={(v) => setTab(v as "read" | "history")} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="mx-4 mt-2 w-fit">
                <TabsTrigger value="read"><Eye className="h-4 w-4 mr-1" /> Reading</TabsTrigger>
                <TabsTrigger value="history"><History className="h-4 w-4 mr-1" /> History ({revisions.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="read" className="flex-1 overflow-hidden mt-0">
                <ScrollArea className="h-full">
                  <div className="mx-auto max-w-3xl px-8 py-6 space-y-6">
                    {selected.status === "active" && !selected.reviewed_at && (
                      <div className="text-xs text-muted-foreground italic">
                        Published without review sign-off.
                      </div>
                    )}

                    {openSectionFlags.length > 0 && (
                      <div className="space-y-2">
                        {openSectionFlags.map((f) => (
                          <div key={f.id} className="border border-amber-300 bg-amber-50 dark:bg-amber-950/20 rounded-md p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="text-sm">
                                <p className="whitespace-pre-wrap">{f.note}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {format(new Date(f.created_at), "PPp")}
                                </p>
                              </div>
                              <Button size="sm" variant="outline" onClick={() => resolveFlag(f.id)}>Resolve</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {resolvedSectionFlags.length > 0 && (
                      <div>
                        <button
                          className="text-xs text-muted-foreground hover:underline flex items-center gap-1"
                          onClick={() => setResolvedExpanded((v) => !v)}
                        >
                          <ChevronRight className={`h-3 w-3 transition-transform ${resolvedExpanded ? "rotate-90" : ""}`} />
                          {resolvedSectionFlags.length} resolved flag{resolvedSectionFlags.length === 1 ? "" : "s"}
                        </button>
                        {resolvedExpanded && (
                          <div className="mt-2 space-y-2">
                            {resolvedSectionFlags.map((f) => (
                              <div key={f.id} className="border rounded-md p-2 text-xs text-muted-foreground">
                                <p className="whitespace-pre-wrap">{f.note}</p>
                                <p className="mt-1">Resolved {f.resolved_at ? format(new Date(f.resolved_at), "PPp") : ""}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {editing ? (
                      <div className="space-y-3">
                        <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="text-xl font-semibold" />
                        <Textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="min-h-[400px] font-mono text-sm"
                        />
                        <Input
                          placeholder="What changed and why"
                          value={editNote}
                          onChange={(e) => setEditNote(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <Button onClick={() => saveEdit()} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                            Save
                          </Button>
                          <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <article
                        style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                        className="prose-reading"
                      >
                        <h1 className="text-3xl font-semibold mb-4" style={{ fontFamily: "inherit" }}>{selected.title}</h1>
                        <div className="whitespace-pre-wrap text-[17px] leading-8">
                          {selected.content}
                        </div>
                      </article>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="history" className="flex-1 overflow-hidden mt-0">
                <ScrollArea className="h-full">
                  <div className="mx-auto max-w-3xl px-8 py-6 space-y-3">
                    {revisions.length === 0 && (
                      <p className="text-sm text-muted-foreground">No revisions yet.</p>
                    )}
                    {revisions.map((r) => (
                      <div key={r.id} className="border rounded-md p-3 flex items-start justify-between gap-3">
                        <div className="text-sm">
                          <div className="font-medium">{r.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {format(new Date(r.created_at), "PPpp")} · {r.edited_by ? (profilesMap[r.edited_by] || "Admin") : "System"}
                          </div>
                          {r.edit_note && <p className="text-xs mt-1 italic">{r.edit_note}</p>}
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setViewingRevision(r)}>View</Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </>
        )}
      </Card>

      {/* Flag dialog */}
      <Dialog open={flagOpen} onOpenChange={setFlagOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Flag an issue</DialogTitle>
            <DialogDescription>Describe what needs attention in this section.</DialogDescription>
          </DialogHeader>
          <Textarea value={flagNote} onChange={(e) => setFlagNote(e.target.value)} className="min-h-[120px]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagOpen(false)}>Cancel</Button>
            <Button onClick={submitFlag} disabled={!flagNote.trim()}>Add flag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish confirm */}
      <Dialog open={publishConfirm} onOpenChange={setPublishConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish this section?</DialogTitle>
            <DialogDescription>
              Publishing makes this section available to Public Stew immediately.
            </DialogDescription>
          </DialogHeader>
          {selected && !selected.reviewed_at && (
            <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span>This section is not marked reviewed.</span>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishConfirm(false)} disabled={publishBusy}>Cancel</Button>
            <Button onClick={doPublishToggle} disabled={publishBusy}>
              {publishBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revision viewer */}
      <Dialog open={!!viewingRevision} onOpenChange={(o) => !o && setViewingRevision(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{viewingRevision?.title}</DialogTitle>
            <DialogDescription>
              {viewingRevision && format(new Date(viewingRevision.created_at), "PPpp")}
              {viewingRevision?.edit_note && ` · ${viewingRevision.edit_note}`}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1">
            <div
              className="whitespace-pre-wrap text-[16px] leading-7 px-1"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {viewingRevision?.content}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingRevision(null)}>Close</Button>
            <Button onClick={() => viewingRevision && restoreRevision(viewingRevision)} disabled={saving}>
              <RotateCcw className="h-4 w-4 mr-1" /> Restore this version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Confirm switch section with unsaved changes */}
      <AlertDialog open={!!pendingSelectId} onOpenChange={(o) => !o && setPendingSelectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved edits in this section. Switching will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSelectPending}>Discard and switch</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm cancel edit */}
      <AlertDialog open={confirmCancelEdit} onOpenChange={setConfirmCancelEdit}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Your edits will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardEdit}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm restore revision */}
      <AlertDialog open={!!confirmRestore} onOpenChange={(o) => !o && setConfirmRestore(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this version?</AlertDialogTitle>
            <AlertDialogDescription>
              It becomes the current content as a new revision.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmRestore && doRestoreRevision(confirmRestore)}>
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
